import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Stack,
  Chip,
} from '@mui/material';
import { PendingChannel, REJECTION_REASONS, RejectionReason } from '../../types/pendingChannels';
import { rejectPendingChannel, batchUpdatePendingChannels } from '../../services/firestoreChannelService';
import { useAuth } from '../../contexts/AuthContext';

interface BulkRejectPendingChannelsModalProps {
  open: boolean;
  channels: (PendingChannel & { _id: string })[];
  onClose: () => void;
  onSuccess: (result: BulkRejectionResult) => void;
}

export interface BulkRejectionResult {
  rejected: number;
  failed: number;
  errors: Array<{ channel_id: string; error: string }>;
}

export const BulkRejectPendingChannelsModal: React.FC<BulkRejectPendingChannelsModalProps> = ({
  open,
  channels,
  onClose,
  onSuccess,
}) => {
  const [rejectionReason, setRejectionReason] = useState<RejectionReason>(
    REJECTION_REASONS.NOT_RELEVANT
  );
  const [rejectionNotes, setRejectionNotes] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { currentUser } = useAuth();

  const handleReject = async () => {
    if (!currentUser || channels.length === 0) return;

    // Validate that "other" reason has notes
    if (rejectionReason === REJECTION_REASONS.OTHER && !rejectionNotes.trim()) {
      setError('Notes are required when rejection reason is "other"');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result: BulkRejectionResult = {
        rejected: 0,
        failed: 0,
        errors: [],
      };

      // Process each channel with individual rejection for better error handling
      const rejectionPromises = channels.map(async (channel) => {
        try {
          await rejectPendingChannel(
            channel._id,
            rejectionReason,
            currentUser.email || currentUser.uid,
            rejectionNotes.trim() || undefined
          );
          result.rejected++;
        } catch (err) {
          result.failed++;
          result.errors.push({
            channel_id: channel._id,
            error: err instanceof Error ? err.message : 'Unknown error',
          });
        }
      });

      await Promise.all(rejectionPromises);

      onSuccess(result);
      // Reset form
      setRejectionReason(REJECTION_REASONS.NOT_RELEVANT);
      setRejectionNotes('');
      onClose();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to reject channels';
      setError(errorMessage);
      console.error('Error in bulk rejection:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setRejectionReason(REJECTION_REASONS.NOT_RELEVANT);
      setRejectionNotes('');
      setError(null);
      onClose();
    }
  };

  const rejectionReasonOptions = Object.values(REJECTION_REASONS);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>
        Reject {channels.length} Channel{channels.length !== 1 ? 's' : ''}
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>
          {/* Selected Channels Summary */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Selected Channels ({channels.length})
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {channels.slice(0, 5).map((channel) => (
                <Chip
                  key={channel._id}
                  label={channel.title}
                  size="small"
                  variant="outlined"
                  onDelete={undefined}
                />
              ))}
              {channels.length > 5 && (
                <Chip
                  label={`+${channels.length - 5} more`}
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>
          </Box>

          {/* Error Alert */}
          {error && <Alert severity="error">{error}</Alert>}

          {/* Rejection Reason */}
          <FormControl fullWidth size="small">
            <InputLabel>Rejection Reason</InputLabel>
            <Select
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value as RejectionReason)}
              label="Rejection Reason"
              disabled={isLoading}
            >
              {rejectionReasonOptions.map((reason) => (
                <MenuItem key={reason} value={reason}>
                  {reason.charAt(0).toUpperCase() + reason.slice(1).replace(/_/g, ' ')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Rejection Notes */}
          {rejectionReason === REJECTION_REASONS.OTHER && (
            <TextField
              label="Rejection Notes"
              placeholder="Please provide specific notes for the rejection"
              multiline
              rows={4}
              value={rejectionNotes}
              onChange={(e) => setRejectionNotes(e.target.value)}
              disabled={isLoading}
              fullWidth
              required
              helperText="Notes are required for 'Other' rejection reason"
            />
          )}

          {/* Optional Notes for other reasons */}
          {rejectionReason !== REJECTION_REASONS.OTHER && (
            <TextField
              label="Additional Notes (Optional)"
              placeholder="Add any additional context for the rejection"
              multiline
              rows={3}
              value={rejectionNotes}
              onChange={(e) => setRejectionNotes(e.target.value)}
              disabled={isLoading}
              fullWidth
            />
          )}

          {/* Info Alert */}
          <Alert severity="info">
            Rejecting {channels.length} channel{channels.length !== 1 ? 's' : ''} with reason: <strong>{rejectionReason.replace(/_/g, ' ')}</strong>
          </Alert>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleReject}
          variant="contained"
          color="error"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={20} /> : undefined}
        >
          {isLoading ? 'Rejecting...' : 'Reject Channels'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
